import * as React from "react";
import { Button, TextArea } from "@jhool-io/fe-components";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import useToast from "../../../../hooks/useToast";

import { useUpdateClientForm } from "../../hooks/mutations/client-forms.mutations";
import { IClientForm } from "../../types/client-forms.types";
import { formatDate } from "../../../../utils/helpers";
import {
    useDoesUserHavePermission,
    useDoesUserHaveRole,
} from "../../../../hooks/permissions";
import { UserPermisions, UserRole } from "../../../../utils/types/user";

interface FormActivityLogProps {
    form: IClientForm;
    onClose: () => void;
    onFormUpdate?: (updatedForm: IClientForm) => void;
}

const parseDescriptionUpdates = (description: string) => {
    const updates = description
        .split(/,\s*(?![^(]*\))|(?:\s+and\s+)/)
        .map((u) => u.trim())
        .filter((u) => u.length > 0);

    return updates.map((update) =>
        update.replace(/\b(\d{4}-\d{2}-\d{2})\b/g, (match) => {
            const d = new Date(`${match}T00:00:00`);
            return d.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
            });
        })
    );
};

const descriptionToDisplay = (description: string) => {
    const updates = parseDescriptionUpdates(description);
    if (updates.length > 1) {
        return (
            <div className="space-y-1">
                {updates.map((update) => (
                    <div
                        key={update}
                        className="text-sm text-textSec flex items-start"
                    >
                        <span className="mr-2">•</span>
                        <span>{update}</span>
                    </div>
                ))}
            </div>
        );
    }
    return <p className="text-sm text-textSec">{updates[0] || description}</p>;
};

export default function FormActivityLog({
    form,
    onClose,
    onFormUpdate,
}: FormActivityLogProps) {
    const [activityNote, setActivityNote] = React.useState("");

    const { toast } = useToast();
    const queryClient = useQueryClient();
    const updateFormMutation = useUpdateClientForm();

    const hasClientFormWritePermission = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_ALL_WRITE,
        UserPermisions.CLIENT_INFO_PERSONAL_WRITE,
    ]);
    const hasClientFormRole = useDoesUserHaveRole([
        UserRole.SUPER_ADMIN,
        UserRole.CLIENT_SUPPORT,
        UserRole.SUPPORT_GENERAL,
    ]);
    const canWriteClientForms =
        hasClientFormWritePermission && hasClientFormRole;

    const isAddingActivityNote = useIsMutating(["update-client-form"]) > 0;

    const handleSubmitNote = async () => {
        if (!activityNote.trim()) return;

        updateFormMutation.mutate(
            {
                clientId: form.client_id,
                formId: form.form_id,
                data: { activity_note: activityNote.trim() },
            },
            {
                onSuccess: (response) => {
                    if (response.data) onFormUpdate?.(response.data);
                    queryClient.invalidateQueries(["client-forms"]);
                    setActivityNote("");
                    toast({
                        mode: "success",
                        message: "Activity note added successfully",
                    });
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err?.response?.data?.message ||
                            "Could not create form at this time",
                    });
                },
            }
        );
    };

    const handleClose = () => {
        setActivityNote("");
        onClose();
    };

    return (
        <div className="flex flex-col h-full">
            <div className="flex-1 overflow-y-auto mb-6">
                {form && form.activity_log && form.activity_log.length > 0 ? (
                    <div className="space-y-4">
                        {form.activity_log
                            .slice()
                            .reverse()
                            .map((activity) => (
                                <div key={activity.activity_id}>
                                    <div className="bg-[--card-bg] rounded-lg [border:1px_solid_var(--color-stroke)] p-4 shadow-active">
                                        <div className="flex justify-between items-start mb-3">
                                            <span className="text-sm font-semibold text-text">
                                                {activity.user_name}
                                            </span>
                                            <span className="text-xs text-textSec">
                                                {formatDate(
                                                    activity.timestamp,
                                                    false,
                                                    "MMM d, yyyy, h:mm a"
                                                )}
                                            </span>
                                        </div>
                                        {descriptionToDisplay(
                                            activity.description
                                        )}
                                    </div>
                                </div>
                            ))}
                    </div>
                ) : (
                    <div className="text-center py-12">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[--card-bg-gray] mb-4">
                            <svg
                                className="w-8 h-8 text-textSec"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                            </svg>
                        </div>
                        <p className="text-sm text-textSec">
                            No activity history available
                        </p>
                    </div>
                )}
            </div>

            <div className="[border-top:1px_solid_var(--color-stroke)] pt-4 bg-[--card-bg]">
                {canWriteClientForms && (
                    <>
                        <h3 className="text-sm font-semibold text-text mb-3">
                            Add Activity Note
                        </h3>
                        <TextArea
                            value={activityNote}
                            onChange={(e) => setActivityNote(e.target.value)}
                            placeholder="Add a note about this form..."
                            rows={3}
                            className="w-full p-3 border mb-3"
                        />
                    </>
                )}
                <div className="flex gap-2">
                    {canWriteClientForms && (
                        <Button
                            onClick={handleSubmitNote}
                            disabled={
                                !activityNote.trim() || isAddingActivityNote
                            }
                            variant="primary"
                        >
                            {isAddingActivityNote ? "Adding..." : "Add Note"}
                        </Button>
                    )}
                    <Button onClick={handleClose} variant="secondary">
                        Close
                    </Button>
                </div>
            </div>
        </div>
    );
}
